import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Stack, TextField, MenuItem, Button, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, Box
} from '@mui/material';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { api } from '../../api';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function AccountingPage() {
  const { t, i18n } = useTranslation();
  const [checkouts, setCheckouts] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [dateFrom, setDateFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [sortBy, setSortBy] = useState('checkout');
  const [loading, setLoading] = useState(false);

  // 1. Завантаження даних з сервера
  const loadData = async () => {
    setLoading(true);
    try {
      const [c, s, u, b] = await Promise.all([
        api.getCheckouts ? api.getCheckouts() : [],
        api.getServices ? api.getServices() : [],
        api.getUsers ? api.getUsers() : [],
        api.getBookings ? api.getBookings() : [],
      ]);
      setCheckouts(c);
      setServices(s);
      setUsers(u);
      setBookings(b);
    } catch (err) {
      setCheckouts([]);
      setServices([]);
      setUsers([]);
      setBookings([]);
    }
    setLoading(false);
  };

  // 2. Застосування фільтра і сортування
  useEffect(() => {
    if (!checkouts.length) {
      setFiltered([]);
      return;
    }

    const from = dayjs(dateFrom).startOf('day');
    const to = dayjs(dateTo).endOf('day');

    const data = checkouts
      .filter(c => {
        const ts = dayjs(c.timestamp);
        return ts.isSameOrAfter(from) && ts.isSameOrBefore(to);
      })
      .map(c => {
        const booking = bookings.find(b => b.id === c.bookingId);
        const user = users.find(u => u.id === booking?.userId);
        // performedServices може бути або масивом id, або чисел
        const performed = services.filter(s => c.performedServices?.includes(String(s.id)) || c.performedServices?.includes(Number(s.id)));
        const serviceNames = performed.map(s => pickServiceName(s, i18n.language)).join(', ');
        const serviceTotal = performed.reduce((sum, s) => sum + (s.price || 0), 0);

        // Помітка «чому така ціна»: ручна ціна брони та/або знижка по промокоду
        const noteParts = [];
        if (c.bookingPrice != null && c.bookingPrice !== '') noteParts.push(t('accounting.noteManual'));
        if (c.promoCode && Number(c.discountAmount) > 0) {
          noteParts.push(`${t('accounting.notePromo')} ${c.promoCode} (−${formatMoney(Number(c.discountAmount))})`);
        }
        const priceNote = noteParts.join(' · ');

        return {
          ...c,
          priceNote,
          // імʼя/телефон беремо з локального джойну, а якщо не знайшли —
          // із самого чекауту (getCheckouts уже повертає clientName/clientPhone)
          clientName: user?.name || c.clientName || '—',
          clientPhone: user?.phone || c.clientPhone || '',
          serviceNames,
          serviceTotal,
          bookingDate: booking?.date || ''
        };
      })
      .sort((a, b) => {
        if (sortBy === 'booking') {
          return dayjs(a.bookingDate).isAfter(dayjs(b.bookingDate)) ? 1 : -1;
        }
        return dayjs(a.timestamp).isAfter(dayjs(b.timestamp)) ? 1 : -1;
      });

    setFiltered(data);
  }, [checkouts, dateFrom, dateTo, sortBy, services, users, bookings, i18n.language, t]);

  // 3. Для суми за всі записи
  const totalFact = useMemo(() => filtered.reduce((sum, r) => sum + Number(r.actualAmount || 0), 0), [filtered]);
  const totalTips = useMemo(() => filtered.reduce((sum, r) => sum + Number(r.tip || 0), 0), [filtered]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const handleRefresh = () => loadData();

  // --- Генерація PDF-звіту (друк у PDF: повна підтримка pl/uk-символів, без залежностей) ---
  const generatePdf = () => {
    const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const grand = totalFact + totalTips;
    const rowsHtml = filtered.map((r) => {
      const amount = Number(r.actualAmount || 0);
      const tip = Number(r.tip || 0);
      return `<tr>
        <td>${esc(dayjs(r.timestamp).format('YYYY-MM-DD HH:mm'))}</td>
        <td>${esc(r.clientName)}${r.clientPhone ? `<div class="ph">${esc(r.clientPhone)}</div>` : ''}</td>
        <td>${esc(r.serviceNames)}</td>
        <td class="num">${esc(formatMoney(amount))}</td>
        <td class="num">${esc(formatMoney(tip))}</td>
        <td class="num">${esc(formatMoney(amount + tip))}</td>
        <td class="note">${esc(r.priceNote || '')}</td>
      </tr>`;
    }).join('');

    const html = `<!doctype html><html lang="${i18n.language}"><head><meta charset="utf-8">
<title>${esc(t('accounting.reportTitle'))} — Clean Hands Club</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { font-family: Arial, Helvetica, sans-serif; box-sizing: border-box; }
  body { color: #1a1a16; margin: 0; }
  .brand { color: #977536; font-size: 22px; font-weight: 700; letter-spacing: .04em; }
  .sub { color: #57564d; font-size: 12px; margin-top: 2px; }
  h1 { font-size: 17px; margin: 18px 0 4px; }
  .meta { color: #57564d; font-size: 11px; margin-bottom: 14px; }
  .cards { display: flex; gap: 10px; margin: 14px 0 18px; }
  .card { flex: 1; border: 1px solid #e0d6bf; border-radius: 8px; padding: 10px 12px; background: #faf7f2; }
  .card .l { font-size: 10px; text-transform: uppercase; letter-spacing: .08em; color: #8a887f; }
  .card .v { font-size: 17px; font-weight: 700; margin-top: 3px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { text-align: left; background: #efeae2; border-bottom: 2px solid #c9a24a; padding: 7px 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #e8e2d8; vertical-align: top; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  .ph { color: #8a887f; font-size: 10px; }
  td.note { color: #57564d; font-size: 10px; }
  tfoot td { font-weight: 700; border-top: 2px solid #c9a24a; background: #faf7f2; }
  .footer { margin-top: 16px; font-size: 10px; color: #8a887f; text-align: center; }
  tr { break-inside: avoid; }
</style></head><body>
  <div class="brand">Clean Hands Club</div>
  <div class="sub">${esc(t('accounting.reportSubtitle'))}</div>
  <h1>${esc(t('accounting.reportTitle'))}</h1>
  <div class="meta">${esc(t('accounting.reportPeriod'))}: ${esc(dateFrom)} – ${esc(dateTo)} &nbsp;·&nbsp; ${esc(t('accounting.reportGenerated'))}: ${esc(dayjs().format('YYYY-MM-DD HH:mm'))}</div>
  <div class="cards">
    <div class="card"><div class="l">${esc(t('accounting.reportVisits'))}</div><div class="v">${filtered.length}</div></div>
    <div class="card"><div class="l">${esc(t('accounting.reportSum'))}</div><div class="v">${esc(formatMoney(totalFact))}</div></div>
    <div class="card"><div class="l">${esc(t('accounting.reportTips'))}</div><div class="v">${esc(formatMoney(totalTips))}</div></div>
    <div class="card"><div class="l">${esc(t('accounting.reportGrand'))}</div><div class="v">${esc(formatMoney(grand))}</div></div>
  </div>
  <table>
    <thead><tr>
      <th>${esc(t('accounting.colDate'))}</th>
      <th>${esc(t('accounting.colClient'))}</th>
      <th>${esc(t('accounting.colServices'))}</th>
      <th class="num">${esc(t('accounting.colFact'))}</th>
      <th class="num">${esc(t('accounting.colTips'))}</th>
      <th class="num">${esc(t('accounting.colTotal'))}</th>
      <th>${esc(t('accounting.colNote'))}</th>
    </tr></thead>
    <tbody>${rowsHtml || `<tr><td colspan="7" style="text-align:center;color:#8a887f;padding:20px">${esc(t('accounting.noData'))}</td></tr>`}</tbody>
    <tfoot><tr>
      <td colspan="3">${esc(t('accounting.reportGrand'))}</td>
      <td class="num">${esc(formatMoney(totalFact))}</td>
      <td class="num">${esc(formatMoney(totalTips))}</td>
      <td class="num">${esc(formatMoney(grand))}</td>
      <td></td>
    </tr></tfoot>
  </table>
  <div class="footer">Clean Hands Club — ${esc(t('accounting.reportFooter'))}</div>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) return; // попап заблоковано
    w.document.write(html);
    w.document.close();
    w.focus();
    // Даємо браузеру відрендерити, тоді відкриваємо діалог друку (там «Зберегти як PDF»)
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 300);
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#1a1a16' }}>
        {t('accounting.title')}
      </Typography>

      <Paper sx={{ p: 2, mb: 3, bgcolor: '#faf7f2', border: '1px solid rgba(28,29,23,0.18)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label={t('accounting.dateFrom')}
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ input: { color: '#1a1a16' }, label: { color: '#57564d' } }}
          />
          <TextField
            label={t('accounting.dateTo')}
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ input: { color: '#1a1a16' }, label: { color: '#57564d' } }}
          />
          <TextField
            label={t('accounting.sortBy')}
            select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            sx={{ minWidth: 180, color: '#1a1a16' }}
          >
            <MenuItem value="checkout">{t('accounting.sortByCheckout')}</MenuItem>
            <MenuItem value="booking">{t('accounting.sortByBooking')}</MenuItem>
          </TextField>
          <Button variant="outlined" onClick={handleRefresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button
            variant="contained"
            onClick={generatePdf}
            disabled={loading || filtered.length === 0}
            sx={{ bgcolor: '#c9a24a', color: '#1a1a16', fontWeight: 600, textTransform: 'none',
                  boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#977536' } }}
          >
            📄 {t('accounting.generatePdf')}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ bgcolor: '#faf7f2', color: '#1a1a16', border: '1px solid rgba(28,29,23,0.18)', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#efeae2' }}>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colCheckoutDate')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colBookingDate')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colClient')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('common.phone')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colServices')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colAmount')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colTips')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colFact')}</TableCell>
              <TableCell sx={{ color: '#1a1a16' }}>{t('accounting.colNote')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{dayjs(r.timestamp).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>{r.bookingDate ? dayjs(r.bookingDate).format('YYYY-MM-DD') : ''}</TableCell>
                <TableCell>{r.clientName}</TableCell>
                <TableCell>{r.clientPhone}</TableCell>
                <TableCell>{r.serviceNames}</TableCell>
                <TableCell>{formatMoney(r.serviceTotal)}</TableCell>
                <TableCell>{formatMoney(r.tip || 0)}</TableCell>
                <TableCell>{formatMoney(r.actualAmount)}</TableCell>
                <TableCell sx={{ color: '#57564d', fontSize: '0.82rem' }}>{r.priceNote}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: '#57564d', py: 4 }}>
                  {t('accounting.noData')}
                </TableCell>
              </TableRow>
            )}
            {/* Підсумковий рядок */}
            {filtered.length > 0 && (
              <TableRow>
                <TableCell colSpan={6} align="right" sx={{ color: '#1a1a16', fontWeight: 'bold' }}>
                  {t('accounting.total')}
                </TableCell>
                <TableCell sx={{ color: '#1a1a16', fontWeight: 'bold' }}>{formatMoney(totalTips)}</TableCell>
                <TableCell sx={{ color: '#1a1a16', fontWeight: 'bold' }}>{formatMoney(totalFact)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}
